import React from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowRight,
  FiSearch,
  FiLayers,
  FiSettings,
  FiZap,
  FiActivity,
  FiUsers,
  FiDatabase,
  FiCpu,
  FiGitBranch,
  FiCheck,
  FiBarChart2,
  FiMessageSquare,
  FiTarget,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./HowItWorks.css";

const HowItWorks = () => {
  return (
    <>
      <Helmet>
        <title>How It Works | Dropgear IO</title>

        <meta
          name="description"
          content="Discover how Dropgear IO helps businesses identify workflows, connect data, deploy AI automation, and continuously improve operations."
        />
      </Helmet>

      <Header />

      <main className="dg-howwork-page">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="dg-howwork-hero">

          <div className="dg-howwork-container dg-howwork-hero-grid">

            <div className="dg-howwork-hero-content">

              <span className="dg-howwork-eyebrow">
                HOW DROPGEAR IO WORKS
              </span>

              <h1>
                From business workflow
                <span> to intelligent action.</span>
              </h1>

              <p>
                Dropgear IO helps organizations identify repetitive work,
                connect the information behind it, introduce AI where it
                creates value, and turn everyday processes into intelligent
                workflows.
              </p>

              <div className="dg-howwork-hero-buttons">

                <a
                  href="/book-demo"
                  className="dg-howwork-btn-primary"
                >
                  Book a Demo
                  <FiArrowRight />
                </a>

                <a
                  href="#dg-howwork-process"
                  className="dg-howwork-btn-secondary"
                >
                  Explore the Process
                </a>

              </div>

              <div className="dg-howwork-hero-points">

                <span>
                  <FiCheck />
                  Start with one workflow
                </span>

                <span>
                  <FiCheck />
                  Keep people involved
                </span>

                <span>
                  <FiCheck />
                  Expand as value grows
                </span>

              </div>

            </div>


            {/* HERO VISUAL */}

            <div className="dg-howwork-hero-visual">

              <div className="dg-howwork-flow-card">

                <div className="dg-howwork-flow-header">

                  <div>

                    <span>
                      Dropgear IO
                    </span>

                    <strong>
                      Intelligent Workflow
                    </strong>

                  </div>

                  <div className="dg-howwork-live">
                    <i></i>
                    Live
                  </div>

                </div>


                <div className="dg-howwork-flow">

                  <div className="dg-howwork-flow-node">

                    <span>
                      <FiDatabase />
                    </span>

                    <div>
                      <strong>
                        Business Data
                      </strong>

                      <small>
                        Information enters
                      </small>
                    </div>

                  </div>


                  <div className="dg-howwork-flow-connector"></div>


                  <div className="dg-howwork-flow-node active">

                    <span>
                      <FiCpu />
                    </span>

                    <div>
                      <strong>
                        AI Processing
                      </strong>

                      <small>
                        Understand & classify
                      </small>
                    </div>

                  </div>


                  <div className="dg-howwork-flow-connector"></div>


                  <div className="dg-howwork-flow-node">

                    <span>
                      <FiGitBranch />
                    </span>

                    <div>
                      <strong>
                        Workflow
                      </strong>

                      <small>
                        Route the next action
                      </small>
                    </div>

                  </div>


                  <div className="dg-howwork-flow-connector"></div>


                  <div className="dg-howwork-flow-node success">

                    <span>
                      <FiCheck />
                    </span>

                    <div>
                      <strong>
                        Completed
                      </strong>

                      <small>
                        Action finished
                      </small>
                    </div>

                  </div>

                </div>


                <div className="dg-howwork-flow-footer">

                  <div>
                    <span>Workflow efficiency</span>
                    <strong>+34%</strong>
                  </div>

                  <div className="dg-howwork-progress">
                    <span></span>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="dg-howwork-intro">

          <div className="dg-howwork-container">

            <div className="dg-howwork-section-heading">

              <span className="dg-howwork-eyebrow">
                A PRACTICAL APPROACH
              </span>

              <h2>
                AI should fit the way
                <span> your business already works.</span>
              </h2>

              <p>
                Instead of forcing organizations to completely change their
                processes, Dropgear IO starts with existing workflows and
                identifies where intelligent automation can make them faster,
                clearer, and more effective.
              </p>

            </div>

          </div>

        </section>


        {/* =====================================================
            PROCESS
        ===================================================== */}

        <section
          className="dg-howwork-process"
          id="dg-howwork-process"
        >

          <div className="dg-howwork-container">

            <div className="dg-howwork-section-heading dg-howwork-centered">

              <span className="dg-howwork-eyebrow">
                THE DROPGEAR METHOD
              </span>

              <h2>
                Four steps from
                <span> workflow to intelligence.</span>
              </h2>

              <p>
                Every implementation begins with understanding the work,
                followed by designing, deploying, and improving the
                intelligence around it.
              </p>

            </div>


            <div className="dg-howwork-process-grid">

              {/* 01 */}

              <div className="dg-howwork-process-card">

                <div className="dg-howwork-step-number">
                  01
                </div>

                <div className="dg-howwork-step-icon">
                  <FiSearch />
                </div>

                <span className="dg-howwork-step-label">
                  DISCOVER
                </span>

                <h3>
                  Understand the workflow
                </h3>

                <p>
                  We first understand how your teams currently work, where
                  information moves, and which repetitive tasks consume the
                  most time.
                </p>

                <div className="dg-howwork-step-list">

                  <span>
                    <FiCheck />
                    Map existing processes
                  </span>

                  <span>
                    <FiCheck />
                    Identify repetitive work
                  </span>

                  <span>
                    <FiCheck />
                    Find automation opportunities
                  </span>

                </div>

              </div>


              {/* 02 */}

              <div className="dg-howwork-process-card">

                <div className="dg-howwork-step-number">
                  02
                </div>

                <div className="dg-howwork-step-icon">
                  <FiLayers />
                </div>

                <span className="dg-howwork-step-label">
                  DESIGN
                </span>

                <h3>
                  Build the intelligence layer
                </h3>

                <p>
                  The workflow is translated into an intelligent system where
                  AI, business rules, information, and human actions work
                  together.
                </p>

                <div className="dg-howwork-step-list">

                  <span>
                    <FiCheck />
                    Connect relevant information
                  </span>

                  <span>
                    <FiCheck />
                    Define AI responsibilities
                  </span>

                  <span>
                    <FiCheck />
                    Design workflow logic
                  </span>

                </div>

              </div>


              {/* 03 */}

              <div className="dg-howwork-process-card">

                <div className="dg-howwork-step-number">
                  03
                </div>

                <div className="dg-howwork-step-icon">
                  <FiZap />
                </div>

                <span className="dg-howwork-step-label">
                  DEPLOY
                </span>

                <h3>
                  Put AI into action
                </h3>

                <p>
                  The intelligent workflow is introduced into the selected
                  business process with appropriate human involvement and
                  operational controls.
                </p>

                <div className="dg-howwork-step-list">

                  <span>
                    <FiCheck />
                    Automate selected actions
                  </span>

                  <span>
                    <FiCheck />
                    Keep human approvals
                  </span>

                  <span>
                    <FiCheck />
                    Monitor workflow activity
                  </span>

                </div>

              </div>


              {/* 04 */}

              <div className="dg-howwork-process-card">

                <div className="dg-howwork-step-number">
                  04
                </div>

                <div className="dg-howwork-step-icon">
                  <FiActivity />
                </div>

                <span className="dg-howwork-step-label">
                  IMPROVE
                </span>

                <h3>
                  Learn and expand
                </h3>

                <p>
                  Once a workflow delivers value, the same approach can be
                  improved and expanded into additional processes and teams.
                </p>

                <div className="dg-howwork-step-list">

                  <span>
                    <FiCheck />
                    Review performance
                  </span>

                  <span>
                    <FiCheck />
                    Improve workflow logic
                  </span>

                  <span>
                    <FiCheck />
                    Expand automation
                  </span>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            WORKFLOW EXAMPLE
        ===================================================== */}

        <section className="dg-howwork-example">

          <div className="dg-howwork-container dg-howwork-example-grid">

            <div className="dg-howwork-example-content">

              <span className="dg-howwork-eyebrow">
                SEE IT IN ACTION
              </span>

              <h2>
                Turn a manual request
                <span> into an intelligent workflow.</span>
              </h2>

              <p>
                Imagine a customer request arriving through your existing
                channel. Instead of moving manually between systems and
                teams, Dropgear IO can help structure the process around AI,
                workflow rules, and human decisions.
              </p>


              <div className="dg-howwork-example-points">

                <div>

                  <span>
                    <FiMessageSquare />
                  </span>

                  <div>

                    <h3>
                      Request received
                    </h3>

                    <p>
                      A new customer request enters the workflow.
                    </p>

                  </div>

                </div>


                <div>

                  <span>
                    <FiCpu />
                  </span>

                  <div>

                    <h3>
                      AI understands
                    </h3>

                    <p>
                      AI analyzes the request and identifies relevant
                      information.
                    </p>

                  </div>

                </div>


                <div>

                  <span>
                    <FiGitBranch />
                  </span>

                  <div>

                    <h3>
                      Workflow decides
                    </h3>

                    <p>
                      Rules determine what should happen next.
                    </p>

                  </div>

                </div>


                <div>

                  <span>
                    <FiUsers />
                  </span>

                  <div>

                    <h3>
                      People review
                    </h3>

                    <p>
                      Teams step in when context or approval is required.
                    </p>

                  </div>

                </div>

              </div>

            </div>


            {/* EXAMPLE DASHBOARD */}

            <div className="dg-howwork-example-dashboard">

              <div className="dg-howwork-example-top">

                <div>

                  <span>
                    Customer Operations
                  </span>

                  <strong>
                    Request #DG-2048
                  </strong>

                </div>

                <span className="dg-howwork-example-status">
                  Processing
                </span>

              </div>


              <div className="dg-howwork-example-timeline">

                <div className="dg-howwork-timeline-item completed">

                  <span>
                    <FiCheck />
                  </span>

                  <div>

                    <strong>
                      Request received
                    </strong>

                    <small>
                      Customer Support
                    </small>

                  </div>

                  <b>
                    Done
                  </b>

                </div>


                <div className="dg-howwork-timeline-line"></div>


                <div className="dg-howwork-timeline-item completed">

                  <span>
                    <FiCpu />
                  </span>

                  <div>

                    <strong>
                      AI classification
                    </strong>

                    <small>
                      Request understood
                    </small>

                  </div>

                  <b>
                    Done
                  </b>

                </div>


                <div className="dg-howwork-timeline-line"></div>


                <div className="dg-howwork-timeline-item current">

                  <span>
                    <FiGitBranch />
                  </span>

                  <div>

                    <strong>
                      Workflow routing
                    </strong>

                    <small>
                      Selecting next action
                    </small>

                  </div>

                  <b>
                    Active
                  </b>

                </div>


                <div className="dg-howwork-timeline-line"></div>


                <div className="dg-howwork-timeline-item">

                  <span>
                    <FiUsers />
                  </span>

                  <div>

                    <strong>
                      Human review
                    </strong>

                    <small>
                      Awaiting team action
                    </small>

                  </div>

                  <b>
                    Pending
                  </b>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            BUILDING BLOCKS
        ===================================================== */}

        <section className="dg-howwork-building">

          <div className="dg-howwork-container">

            <div className="dg-howwork-section-heading dg-howwork-centered">

              <span className="dg-howwork-eyebrow">
                THE INTELLIGENCE LAYER
              </span>

              <h2>
                Everything works together
                <span> around the workflow.</span>
              </h2>

              <p>
                Dropgear IO combines multiple AI and automation capabilities
                into a single operational flow.
              </p>

            </div>


            <div className="dg-howwork-building-grid">

              <div className="dg-howwork-building-card">

                <span>
                  <FiCpu />
                </span>

                <h3>
                  AI Models
                </h3>

                <p>
                  Intelligence for understanding, generating, classifying,
                  summarizing, and reasoning across business tasks.
                </p>

              </div>


              <div className="dg-howwork-building-card">

                <span>
                  <FiDatabase />
                </span>

                <h3>
                  Business Knowledge
                </h3>

                <p>
                  Relevant business information can be brought into the
                  workflow when it is needed.
                </p>

              </div>


              <div className="dg-howwork-building-card">

                <span>
                  <FiGitBranch />
                </span>

                <h3>
                  Workflow Logic
                </h3>

                <p>
                  Rules and process logic determine how work moves from one
                  step to the next.
                </p>

              </div>


              <div className="dg-howwork-building-card">

                <span>
                  <FiUsers />
                </span>

                <h3>
                  Human Decisions
                </h3>

                <p>
                  People remain part of the process wherever context,
                  judgment, or approval matters.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            SCALE
        ===================================================== */}

        <section className="dg-howwork-scale">

          <div className="dg-howwork-container dg-howwork-scale-grid">

            <div className="dg-howwork-scale-visual">

              <div className="dg-howwork-scale-card">

                <div className="dg-howwork-scale-header">

                  <span>
                    AI Operations
                  </span>

                  <FiBarChart2 />

                </div>


                <div className="dg-howwork-scale-bars">

                  <div>
                    <span>Customer Operations</span>
                    <i style={{ width: "82%" }}></i>
                  </div>

                  <div>
                    <span>Finance Operations</span>
                    <i style={{ width: "68%" }}></i>
                  </div>

                  <div>
                    <span>Internal Support</span>
                    <i style={{ width: "74%" }}></i>
                  </div>

                  <div>
                    <span>Sales Operations</span>
                    <i style={{ width: "58%" }}></i>
                  </div>

                </div>


                <div className="dg-howwork-scale-footer">

                  <strong>
                    4 workflows
                  </strong>

                  <span>
                    Ready to expand
                  </span>

                </div>

              </div>

            </div>


            <div className="dg-howwork-scale-content">

              <span className="dg-howwork-eyebrow">
                SCALE WITH PURPOSE
              </span>

              <h2>
                Start small.
                <span> Build intelligently.</span>
              </h2>

              <p>
                You do not need to automate everything at once. Dropgear IO
                allows organizations to begin with one meaningful workflow,
                understand the outcome, and expand from there.
              </p>


              <div className="dg-howwork-scale-list">

                <div>
                  <span>
                    <FiTarget />
                  </span>

                  <div>

                    <h3>
                      Start with a clear use case
                    </h3>

                    <p>
                      Focus on a workflow where AI can create measurable
                      operational value.
                    </p>

                  </div>

                </div>


                <div>
                  <span>
                    <FiActivity />
                  </span>

                  <div>

                    <h3>
                      Learn from the workflow
                    </h3>

                    <p>
                      Observe how teams interact with the system and improve
                      the process over time.
                    </p>

                  </div>

                </div>


                <div>
                  <span>
                    <FiLayers />
                  </span>

                  <div>

                    <h3>
                      Expand across operations
                    </h3>

                    <p>
                      Apply successful patterns to additional workflows and
                      teams.
                    </p>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}

        <section className="dg-howwork-cta">

          <div className="dg-howwork-container">

            <div className="dg-howwork-cta-box">

              <div>

                <span className="dg-howwork-eyebrow">
                  READY TO GET STARTED?
                </span>

                <h2>
                  Find the workflow
                  <span> AI can improve.</span>
                </h2>

                <p>
                  Tell us how your team works today. Together, we can identify
                  where intelligent automation could create practical value.
                </p>

              </div>


              <a
                href="/contact"
                className="dg-howwork-btn-primary"
              >
                Book a Demo
                <FiArrowRight />
              </a>

            </div>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default HowItWorks;